import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LuArrowRight, LuClock, LuExternalLink, LuTreePalm } from 'react-icons/lu';
import SegmentedControl from '../../../components/ui/SegmentedControl';
import { BLOCK_TITLE, DetailItem, EmptyState } from '../../ProfilePage/components/ProfileUi';
import { StatusBadge, TypePill } from '../../LeaveRequests/parts/leaveBits';
import useLeaveText from '../../LeaveRequests/parts/leaveText';
import { daysFromToday } from '../leaveSummary';
import useSmoText from '../smoText';
import { useDuration } from './LeaveOverview';

const FILTERS = [
  { value: 'all', match: () => true },
  { value: 'approved', match: (r) => r.status === 'APPROVED' },
  { value: 'pending', match: (r) => r.status === 'PENDING' },
  { value: 'rejected', match: (r) => r.status === 'REJECTED' },
];

const DATE_FORMAT = { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' };
const DATE_TIME_FORMAT = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' };

/** One leave: type, status, the exact days (and hours), duration and who handled it. */
const LeaveRecord = ({ record }) => {
  const { label } = useSmoText();
  const { formatDate, days } = useLeaveText();
  const duration = useDuration();
  const sameDay = record.start && record.end && record.start.getTime() === record.end.getTime();
  const startsIn = record.start ? daysFromToday(record.start) : null;
  const ongoing = record.status === 'APPROVED' && startsIn !== null && startsIn <= 0 && daysFromToday(record.end) >= 0;
  const upcoming = record.status !== 'REJECTED' && startsIn !== null && startsIn > 0;
  const processed = record.status !== 'PENDING' && (record.processedBy || record.processedAt);

  return (
    <li className={`tw-space-y-3 tw-rounded-2xl tw-border tw-p-3.5 ${ongoing ? 'tw-border-emerald-500/40 tw-bg-emerald-500/[0.05]' : 'tw-border-slate-200/80 tw-bg-white/70 dark:tw-border-white/10 dark:tw-bg-white/[0.03]'}`}>
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <TypePill typeKey={record.leaveType} />
        <StatusBadge status={record.status} />
        {ongoing && <span className="tw-rounded-full tw-bg-emerald-500 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-bold tw-text-white">{label('leaves.record.ongoing')}</span>}
        {upcoming && <span className="tw-rounded-full tw-bg-sky-500/15 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-sky-800 dark:tw-text-sky-200">{label('leaves.record.upcoming')}</span>}
        <span className="tw-ml-auto tw-whitespace-nowrap tw-rounded-lg tw-bg-slate-500/10 tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-tabular-nums tw-text-slate-700 dark:tw-bg-white/[0.06] dark:tw-text-slate-200">
          {duration(record.leaveDays, record.leaveHours)}
        </span>
      </div>

      <p className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">
        <span>{formatDate(record.start, DATE_FORMAT)}</span>
        {!sameDay && (
          <>
            <LuArrowRight aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-text-slate-400" />
            <span>{formatDate(record.end, DATE_FORMAT)}</span>
          </>
        )}
        {record.startTime && record.endTime && (
          <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-lg tw-bg-violet-500/10 tw-px-2 tw-py-0.5 tw-text-xs tw-tabular-nums tw-text-violet-800 dark:tw-text-violet-200">
            <LuClock aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
            {record.startTime} – {record.endTime}
          </span>
        )}
      </p>

      <dl className="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-2 sm:tw-grid-cols-3">
        <DetailItem label={label('leaves.record.requestedAt')}>{formatDate(record.createdAt, DATE_TIME_FORMAT)}</DetailItem>
        <DetailItem label={label('leaves.record.source')}>
          {record.source === 'EmployeeLeaves' ? label('leaves.record.sourceAdmin') : label('leaves.record.sourceRequest')}
        </DetailItem>
        {record.travelDays > 0 && <DetailItem label={label('leaves.record.travel')}>{days(record.travelDays)}</DetailItem>}
        {processed && (
          <DetailItem label={label(record.status === 'REJECTED' ? 'leaves.record.rejectedBy' : 'leaves.record.approvedBy')}>
            {[record.processedBy, record.processedAt && formatDate(record.processedAt, DATE_TIME_FORMAT)].filter(Boolean).join(' · ')}
          </DetailItem>
        )}
        {record.reason && <DetailItem label={label('leaves.record.reason')} wide>{record.reason}</DetailItem>}
        {record.address && <DetailItem label={label('leaves.record.address')} wide>{record.address}</DetailItem>}
        {record.adminNote && record.source === 'LeaveRequests' && <DetailItem label={label('leaves.record.note')} wide>{record.adminNote}</DetailItem>}
      </dl>

      {record.reportFile && (
        <a
          href={record.reportFile.url}
          target="_blank"
          rel="noopener noreferrer"
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-sky-700 hover:tw-underline dark:tw-text-sky-300"
        >
          <LuExternalLink aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
          {label('leaves.record.report', { name: record.reportFile.name || '' })}
        </a>
      )}
    </li>
  );
};

LeaveRecord.propTypes = { record: PropTypes.object.isRequired };

/** The year's leave records, newest first, filterable by status. */
const LeaveRecordList = ({ records, year }) => {
  const { label } = useSmoText();
  const [filter, setFilter] = useState('all');
  const visible = records.filter(FILTERS.find((f) => f.value === filter).match);
  return (
    <section className="tw-space-y-3">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <h3 className={BLOCK_TITLE}>{label('leaves.listTitle', { count: records.length })}</h3>
        <SegmentedControl
          size="sm"
          layoutId="smo-leave-filter"
          label={label('leaves.filterLabel')}
          value={filter}
          onChange={setFilter}
          options={FILTERS.map((f) => ({ value: f.value, label: `${label(`leaves.filter.${f.value}`)} (${records.filter(f.match).length})` }))}
          className="tw-max-w-full tw-overflow-x-auto"
        />
      </div>
      {visible.length === 0 ? (
        <EmptyState compact icon={LuTreePalm} title={records.length === 0 ? label('leaves.empty', { year }) : label('leaves.emptyFilter')} />
      ) : (
        <ul className="tw-space-y-2.5">
          {visible.map((record) => <LeaveRecord key={`${record.source}-${record.id}`} record={record} />)}
        </ul>
      )}
    </section>
  );
};

LeaveRecordList.propTypes = { records: PropTypes.array.isRequired, year: PropTypes.number.isRequired };

export default LeaveRecordList;
